import { Box, Card, CardBody, Flex, HStack, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, SimpleGrid, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { ReactNode } from "react";
import { adminGetMedicineByEntryLog } from "../../../api";
import { colors } from "../../../styles";
import { IAdminMedicineBySearchLogs } from "../../../types";

interface MedicineModalProps {
    isOpen: boolean;
    onClose: () => void;
    medicineId: string;
}

type PathKey = keyof IAdminMedicineBySearchLogs["analysis"]["path"];

const PATH_ITEMS: { key: PathKey; label: string; color: string }[] = [
    { key: "search_list", label: "검색 목록", color: colors.blue },
    { key: "similar_identical", label: "동일 성분", color: colors.covalt },
    { key: "similar_recommend", label: "유사 추천", color: colors.teal },
    { key: "keyword_list", label: "키워드 목록", color: colors.link },
];

function safeNumber(value: number | undefined) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatCount(value: number) {
    return Math.round(value).toLocaleString("ko-KR");
}

function formatShare(part: number, total: number) {
    if (total <= 0 || part <= 0) return "0%";
    const percent = (part / total) * 100;
    if (percent < 0.1) return "<0.1%";
    return `${(Math.round(percent * 10) / 10).toLocaleString("ko-KR", { maximumFractionDigits: 1 })}%`;
}

function ratioWidth(value: number, max: number) {
    if (max <= 0 || value <= 0) return "0%";
    return `${(value / max) * 100}%`;
}

function packImageSrc(packImage: string | undefined) {
    const first = packImage?.split(";")[0];
    if (!first || first === "NULL") return "/png/nutrients.png";
    return first;
}

function AnalysisCard({ children }: { children: ReactNode }) {
    return (
        <Card
            width={"100%"}
            height={"100%"}
            border={`0.5px solid ${colors.g4}`}
            borderRadius={10}
            shadow={"md"}
            backgroundColor={colors.white}
        >
            <CardBody>{children}</CardBody>
        </Card>
    );
}

function StatCard({ label, value }: { label: string; value: string }) {
    return (
        <AnalysisCard>
            <VStack width={"100%"} minH={"108px"} justifyContent={"center"} alignItems={"flex-start"} spacing={3}>
                <Text fontSize={16} fontWeight={500} color={colors.black}>{label}</Text>
                <Text fontSize={40} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {value}
                </Text>
            </VStack>
        </AnalysisCard>
    );
}

function PathCard({ path }: { path: IAdminMedicineBySearchLogs["analysis"]["path"] | undefined }) {
    const rows = PATH_ITEMS.map((item) => ({
        ...item,
        value: safeNumber(path?.[item.key]),
    }));
    const total = rows.reduce((sum, row) => sum + row.value, 0);

    return (
        <AnalysisCard>
            <VStack align={"stretch"} spacing={4}>
                <HStack align={"baseline"} spacing={2}>
                    <Text fontSize={13} fontWeight={500} color={colors.g2}>전체</Text>
                    <Text fontSize={36} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCount(total)}
                    </Text>
                </HStack>

                <Flex
                    height={"10px"}
                    width={"100%"}
                    backgroundColor={colors.g5}
                    borderRadius={"full"}
                    overflow={"hidden"}
                    role={"img"}
                    aria-label={rows.map((row) => `${row.label} ${formatShare(row.value, total)} ${formatCount(row.value)}`).join(", ")}
                >
                    {rows.map((row) => row.value > 0 ? (
                        <Tooltip
                            key={row.key}
                            label={`${row.label} ${formatShare(row.value, total)} (${formatCount(row.value)})`}
                            placement={"top"}
                            hasArrow
                            openDelay={200}
                        >
                            <Box flex={row.value} minW={0} height={"100%"} backgroundColor={row.color} />
                        </Tooltip>
                    ) : null)}
                </Flex>

                <VStack align={"stretch"} spacing={0.5}>
                    {rows.map((row) => (
                        <HStack
                            key={row.key}
                            spacing={3}
                            px={2}
                            py={1.5}
                            mx={-2}
                            borderRadius={"md"}
                            _hover={{ backgroundColor: colors.g5 }}
                        >
                            <HStack width={"108px"} flexShrink={0} spacing={2}>
                                <Box boxSize={"8px"} borderRadius={"full"} backgroundColor={row.color} flexShrink={0} />
                                <Text fontSize={13} fontWeight={500} color={colors.g1} noOfLines={1}>{row.label}</Text>
                            </HStack>
                            <Box flex={1} height={"8px"} backgroundColor={colors.g5} borderRadius={"full"} overflow={"hidden"}>
                                <Box
                                    height={"100%"}
                                    width={ratioWidth(row.value, total)}
                                    backgroundColor={row.color}
                                    borderRadius={"full"}
                                />
                            </Box>
                            <Box
                                flexShrink={0}
                                minW={"148px"}
                                textAlign={"right"}
                                fontSize={13}
                                fontWeight={700}
                                color={colors.black}
                                whiteSpace={"nowrap"}
                                sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                                {formatShare(row.value, total)}
                                <Text as={"span"} fontWeight={400} color={colors.g2}> ({formatCount(row.value)})</Text>
                            </Box>
                        </HStack>
                    ))}
                </VStack>
            </VStack>
        </AnalysisCard>
    );
}

function RankTable({ rows }: { rows: { label: string; count: number }[] }) {
    const topRows = rows.slice(0, 5);

    return (
        <TableContainer>
            <Table variant={"simple"} size={"sm"}>
                <Thead backgroundColor={colors.g5}>
                    <Tr>
                        <Th>순위</Th>
                        <Th>이름</Th>
                        <Th isNumeric>횟수</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {topRows.length > 0 ? topRows.map((row, index) => (
                        <Tr key={`${row.label}-${index}`}>
                            <Td>{index + 1}</Td>
                            <Td>{row.label || "-"}</Td>
                            <Td isNumeric>{formatCount(safeNumber(row.count))}</Td>
                        </Tr>
                    )) : (
                        <Tr>
                            <Td colSpan={3} color={colors.g2}>데이터 없음</Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

export default function MedicineModal(props: MedicineModalProps) {

    const { data, isLoading } = useQuery<IAdminMedicineBySearchLogs>({
        queryKey: ["adminGetMedicineByEntryLog", props.medicineId],
        queryFn: () => adminGetMedicineByEntryLog({ medicineId: props.medicineId }),
        enabled: !!props.medicineId && props.isOpen,
    });

    const medicine = data?.medicine;
    const analysis = data?.analysis;

    return (
        <Modal motionPreset="slideInBottom" size={"4xl"} scrollBehavior="inside" isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text color={colors.blue} fontSize="lg" fontWeight="bold">
                        {medicine?.nameKor ?? "-"}
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody paddingTop={10} paddingBottom={10}>
                    {isLoading ? (
                        <Stack width={"100%"} minH={"240px"} justifyContent={"center"} alignItems={"center"}>
                            <Spinner color={colors.blue} />
                        </Stack>
                    ) : (
                        <Stack spacing={8}>
                            <HStack width={"100%"} justifyContent={"flex-start"} alignItems={"flex-start"} padding={5} spacing={0}>
                                <Box
                                    width="120px"
                                    height="120px"
                                    minWidth="120px"
                                    minHeight="120px"
                                    maxWidth="120px"
                                    maxHeight="120px"
                                    border={"0.2px solid " + colors.g4}
                                    borderRadius="8px"
                                    overflow="hidden"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Image
                                        src={packImageSrc(medicine?.packImage)}
                                        alt="medicine"
                                        width="100%"
                                        height="100%"
                                        objectFit={"contain"}
                                    />
                                </Box>
                                <VStack
                                    justifyContent={"center"}
                                    alignItems={"flex-start"}
                                    marginLeft={5}
                                    spacing={2}
                                    flex={1}
                                    minWidth={0}
                                >
                                    <Text fontSize={14} fontWeight={700} color={colors.g3}>
                                        {medicine?.seller || "-"} / {medicine?.maker || "-"}
                                    </Text>
                                    <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                        {medicine?.nameKor || "-"}
                                    </Text>
                                    <Text fontSize={12} fontWeight={400} color={colors.g1}>
                                        {medicine?.packageUnit || "-"}
                                    </Text>
                                    <Text fontSize={12} fontWeight={400} color={colors.g2}>
                                        표준코드 {medicine?.standardCode || "-"}
                                    </Text>
                                    <Text fontSize={12} fontWeight={400} color={colors.g1} whiteSpace={"pre-wrap"}>
                                        {medicine?.note || "-"}
                                    </Text>
                                    <HStack flexWrap={"wrap"} spacing={2}>
                                        {(medicine?.keywords ?? []).length > 0
                                            ? medicine?.keywords.map((keyword, index) => (
                                                <Text key={`${keyword}-${index}`} fontSize={12} fontWeight={500} color={colors.blue}>#{keyword}</Text>
                                            ))
                                            : <Text fontSize={12} color={colors.g3}>연결된 키워드 없음</Text>}
                                    </HStack>
                                    <HStack flexWrap={"wrap"} spacing={2}>
                                        {(medicine?.tags ?? []).length > 0
                                            ? medicine?.tags.map((tag, index) => (
                                                <Text key={`${tag}-${index}`} fontSize={12} fontWeight={500} color={colors.link}>#{tag}</Text>
                                            ))
                                            : <Text fontSize={12} color={colors.g3}>태그 없음</Text>}
                                    </HStack>
                                </VStack>
                            </HStack>

                            <SimpleGrid columns={{ base: 1, md: 2 }} gap={5}>
                                <StatCard label={"진입 수"} value={formatCount(safeNumber(analysis?.entryCount))} />
                                <StatCard label={"진입 유저 수"} value={formatCount(safeNumber(analysis?.userCount))} />
                            </SimpleGrid>

                            <Stack spacing={4}>
                                <Text fontSize={18} fontWeight={700} color={colors.black}>진입 경로</Text>
                                <PathCard path={analysis?.path} />
                            </Stack>

                            <Stack spacing={4}>
                                <Text fontSize={18} fontWeight={700} color={colors.black}>검색어 TOP 5</Text>
                                <RankTable
                                    rows={(analysis?.rank.searchKeyword ?? []).map((item) => ({
                                        label: item.searchKeyword,
                                        count: item.count,
                                    }))}
                                />
                            </Stack>

                            <Stack spacing={4}>
                                <Text fontSize={18} fontWeight={700} color={colors.black}>키워드 TOP 5</Text>
                                <RankTable
                                    rows={(analysis?.rank.keyword ?? []).map((item) => ({
                                        label: item.keyword,
                                        count: item.count,
                                    }))}
                                />
                            </Stack>
                        </Stack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
